import { readConfig, writeConfig, type CliConfig } from "./config.js";
import { discoverGateway } from "./discovery.js";

export interface GatewayContext {
  gatewayUrl: string;
  gatewayClientId: string;
  gatewayTenantId: string;
  subscriptionId: string;
  containerAppResourceId: string;
  /** Was this resolved from cache or freshly discovered? */
  fromCache: boolean;
}

/** Extract the resource-group name from a container-app resource id. */
function rgOf(resourceId: string): string {
  return resourceId.split("/resourceGroups/")[1]?.split("/")[0] ?? "";
}

/**
 * Resolve the gateway context: prefer cached config; fall back to ARM
 * discovery. On discovery, writes the result to
 * ~/.m8t/cli-config.yaml.
 *
 * If `subscriptionId` is provided AND differs from the cached
 * subscriptionId, the cache is skipped and discovery runs against the
 * requested subscription. The same flag is forwarded
 * to discoverGateway so `m8t <cmd> --subscription <other>` works even
 * with no cache present.
 *
 * If `resourceGroup` is provided AND the cached gateway is NOT in that RG,
 * the cache is skipped and discovery re-runs with the RG filter (W-A).
 */
export async function resolveGatewayContext(opts: {
  interactive: boolean;
  subscriptionId?: string;
  resourceGroup?: string;
  forceRediscover?: boolean;
}): Promise<GatewayContext> {
  const cached = opts.forceRediscover ? null : await readConfig();

  if (
    cached !== null &&
    (opts.subscriptionId === undefined || cached.subscriptionId === opts.subscriptionId) &&
    (opts.resourceGroup === undefined ||
      rgOf(cached.containerAppResourceId).toLowerCase() === opts.resourceGroup.toLowerCase())
  ) {
    return {
      gatewayUrl: cached.gatewayUrl,
      gatewayClientId: cached.gatewayClientId,
      gatewayTenantId: cached.gatewayTenantId,
      subscriptionId: cached.subscriptionId,
      containerAppResourceId: cached.containerAppResourceId,
      fromCache: true,
    };
  }

  const fresh = await discoverGateway({
    interactive: opts.interactive,
    subscriptionId: opts.subscriptionId,
    resourceGroup: opts.resourceGroup,
  });
  const toCache: CliConfig = {
    gatewayUrl: fresh.gatewayUrl,
    gatewayClientId: fresh.gatewayClientId,
    gatewayTenantId: fresh.gatewayTenantId,
    subscriptionId: fresh.subscriptionId,
    containerAppResourceId: fresh.containerAppResourceId,
    cachedAt: new Date().toISOString(),
  };
  await writeConfig(toCache);
  return { ...fresh, fromCache: false };
}
